import { type ReactNode, type RefObject } from "react";
import type { PickerListItem } from "@/features/sparxsEditor/components/molecules/pickerList/pickerList";
import { PICKER_LIST_INSIDE_PAPER_CLASS } from "@/features/sparxsEditor/components/organisms/richTextEditor/variables/variableTokenConstants";
import { CONSOLE_OVERLAY_PANEL_CLASS } from "@/lib/ui/consoleOverlayPanel";
import { cn } from "@/lib/utils";
import { DirectiveSegmentPickerList } from "./segmentPickerList";
import type { DirectiveBuilderStep } from "../../suggestion/storage/session";

export type DirectiveBuilderListPanelProps = {
  step: DirectiveBuilderStep;
  stepTitle: string | undefined;
  isChip: boolean;
  items: PickerListItem[];
  onSelectItem: (item: PickerListItem) => void;
  selectedIndex: number;
  onListHighlightChange?: (index: number) => void;
  listOptionIdPrefix?: string;
  listClassName?: string;
  emptyMessage?: ReactNode;
  listContainerRef?: RefObject<HTMLDivElement | null>;
  firstItemRef?: RefObject<HTMLDivElement | null>;
  onListEscape?: () => void;
  listFilterText: string;
  onListFilterChange?: (value: string) => void;
  listFilterRef: RefObject<HTMLInputElement | null>;
};

export const DirectiveBuilderListPanel = ({
  step,
  stepTitle,
  isChip,
  items,
  onSelectItem,
  selectedIndex,
  onListHighlightChange,
  listOptionIdPrefix,
  listClassName,
  emptyMessage,
  listContainerRef,
  firstItemRef,
  onListEscape,
  listFilterText,
  onListFilterChange,
  listFilterRef,
}: DirectiveBuilderListPanelProps): ReactNode => {
  const listSurfaceAttr = isChip ? "chip-list" : "floating-list";
  const outerListClass = isChip
    ? ""
    : cn(
        "z-50 max-h-[280px] min-w-[160px] overflow-auto py-1",
        CONSOLE_OVERLAY_PANEL_CLASS,
      );
  const titleClass = isChip
    ? "mb-2 text-sm text-foreground/45"
    : "mb-2 px-3 text-sm text-foreground/45";

  return (
    <div
      className={outerListClass}
      data-console-overlay-panel=""
      data-directive-session-surface={listSurfaceAttr}
    >
      {stepTitle != null && <p className={titleClass}>{stepTitle}</p>}
      <DirectiveSegmentPickerList
        items={items}
        onSelect={onSelectItem}
        selectedIndex={selectedIndex}
        onHighlightChange={onListHighlightChange}
        optionIdPrefix={listOptionIdPrefix ?? "directive-builder"}
        className={listClassName ?? PICKER_LIST_INSIDE_PAPER_CLASS}
        emptyMessage={emptyMessage}
        containerRef={listContainerRef}
        firstItemRef={firstItemRef}
        onEscape={onListEscape}
        filterValue={listFilterText}
        onFilterChange={onListFilterChange}
        filterInputRef={listFilterRef}
        filterPlaceholder={
          step === "directive" ? "Filter directives" : "Filter variables"
        }
        filterAriaLabel={
          step === "directive"
            ? "Filter directive list"
            : "Filter variable list"
        }
      />
    </div>
  );
};
